import React, { useState, useEffect, useMemo } from 'react';
import { Grid, TextField, MenuItem } from '@mui/material';
import api from '../../../services/api';

let _cache = null;

export function useDepartments() {
  const [departments, setDepartments] = useState(_cache || []);

  useEffect(() => {
    if (_cache) { setDepartments(_cache); return; }
    api.get('/departments').then(res => {
      _cache = res.data;
      setDepartments(res.data);
    }).catch(() => {});
  }, []);

  return departments;
}

/**
 * DepartmentSelect — two linked dropdowns: Department → Sub-department
 *
 * Props:
 *   departmentId     {string}   — current department_id value
 *   subDepartmentId  {string}   — current sub_department_id value
 *   onDepartmentChange(id, name) — called when dept changes
 *   onSubDepartmentChange(id)   — called when sub-dept changes
 *   fieldSx          {object}   — sx passed to TextFields
 *   required         {bool}
 *   deptError        {string}
 *   subError         {string}
 *   gridProps        {object}   — sx for the Grid item wrappers
 */
const DepartmentSelect = ({
  departmentId = '',
  subDepartmentId = '',
  onDepartmentChange,
  onSubDepartmentChange,
  fieldSx = {},
  required = false,
  deptError = '',
  subError = '',
  menuProps = {},
}) => {
  const departments = useDepartments();

  const selectedDept = useMemo(() => departments.find(d => d.id === departmentId) || null, [departments, departmentId]);
  const subDepts = selectedDept?.sub_departments || [];
  const hasSubDepts = subDepts.length > 0;

  const handleDeptChange = (e) => {
    const id = e.target.value;
    const dept = departments.find(d => d.id === id);
    onDepartmentChange(id, dept?.name || '');
    onSubDepartmentChange('');
  };

  // Group departments by type
  const faculties = departments.filter(d => d.type === 'Faculty');
  const nonFaculties = departments.filter(d => d.type !== 'Faculty');

  const renderOptions = () => {
    const items = [];
    if (faculties.length) {
      items.push(<MenuItem key="__fac__" disabled value="" sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 'bold', letterSpacing: 1 }}>── FACULTIES ──</MenuItem>);
      faculties.forEach(d => items.push(
        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
      ));
    }
    if (nonFaculties.length) {
      items.push(<MenuItem key="__nf__" disabled value="" sx={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 'bold', letterSpacing: 1 }}>── NON-FACULTIES ──</MenuItem>);
      nonFaculties.forEach(d => items.push(
        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
      ));
    }
    return items;
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <TextField
          select fullWidth required={required}
          label="Department"
          value={departmentId}
          onChange={handleDeptChange}
          error={!!deptError}
          helperText={deptError}
          sx={fieldSx}
          SelectProps={{ MenuProps: menuProps }}
        >
          <MenuItem value=""><em>Select Department</em></MenuItem>
          {renderOptions()}
        </TextField>
      </Grid>

      {hasSubDepts && (
        <Grid item xs={12} sm={6}>
          <TextField
            select fullWidth
            label="Functional Unit / Sub-Department"
            value={subDepartmentId}
            onChange={e => onSubDepartmentChange(e.target.value)}
            error={!!subError}
            helperText={subError}
            sx={fieldSx}
            SelectProps={{ MenuProps: menuProps }}
          >
            <MenuItem value=""><em>Select Sub-Department</em></MenuItem>
            {subDepts.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
        </Grid>
      )}
    </>
  );
};

export default DepartmentSelect;
